import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { login, mfaVerify, setToken, setupMfa, activateMfa, changeRequiredPassword } from '../api/client'

interface Props {
  onLogin: () => void
}

type LoginStep = 'credentials' | 'mfa_verify' | 'mfa_setup_scan' | 'mfa_setup_confirm' | 'change_password'

export function LoginForm({ onLogin }: Props) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const [step, setStep] = useState<LoginStep>('credentials')
  const [mfaToken, setMfaToken] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [passwordChangeToken, setPasswordChangeToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username || !password) { setError(t('login_form.error_fill_credentials')); return }
    setIsLoading(true)
    setError(null)
    try {
      const result = await login(username, password)
      if (result.mfa_required && result.mfa_token) {
        setMfaToken(result.mfa_token)
        setStep('mfa_verify')
      } else if (result.mfa_setup_required && result.setup_token) {
        setToken(result.setup_token)
        const data = await setupMfa()
        setQrCode(data.qr_code)
        setSecret(data.secret)
        setStep('mfa_setup_scan')
      } else if (result.password_change_required && result.password_change_token) {
        setPasswordChangeToken(result.password_change_token)
        setStep('change_password')
      } else if (result.token) {
        setToken(result.token)
        onLogin()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('login_form.error_login_failed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaCode || !mfaToken) return
    setIsLoading(true)
    setError(null)
    try {
      const token = await mfaVerify(mfaToken, mfaCode)
      setToken(token)
      onLogin()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('login_form.error_verification_failed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSetupConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaCode || mfaCode.length !== 6) return
    setIsLoading(true)
    setError(null)
    try {
      const fullToken = await activateMfa(secret, mfaCode)
      if (fullToken) {
        setToken(fullToken)
        onLogin()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('login_form.error_activation_failed'))
    } finally {
      setIsLoading(false)
    }
  }

  const resetToCredentials = () => {
    setStep('credentials')
    setError(null)
    setMfaCode('')
    setMfaToken('')
    setQrCode('')
    setSecret('')
    setPasswordChangeToken('')
    setNewPassword('')
    setNewPasswordConfirm('')
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== newPasswordConfirm) { setError(t('login_form.error_passwords_mismatch')); return }
    if (newPassword.length < 8) { setError(t('login_form.error_password_too_short')); return }
    setIsLoading(true)
    setError(null)
    try {
      const token = await changeRequiredPassword(passwordChangeToken, newPassword)
      setToken(token)
      onLogin()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('login_form.error_save_failed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: 'url(/banner.png)' }}
    >
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 w-full max-w-sm border border-transparent dark:border-gray-700">
        <div className="flex justify-center mb-4">
          <img src="/logo.png" alt="XML Insights" className="h-12 w-auto" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1 text-center">XML Insights</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {step === 'credentials'     && t('login_form.subtitle_credentials')}
          {step === 'mfa_verify'      && t('login_form.subtitle_mfa_verify')}
          {step === 'mfa_setup_scan'  && t('login_form.subtitle_mfa_setup_scan')}
          {step === 'mfa_setup_confirm' && t('login_form.subtitle_mfa_setup_confirm')}
          {step === 'change_password' && t('login_form.subtitle_change_password')}
        </p>

        {step === 'credentials' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('login_form.label_username')}</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus disabled={isLoading} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('login_form.label_password')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading} />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg text-sm transition-colors">
              {isLoading ? t('login_form.button_busy') : t('login_form.button_login')}
            </button>
          </form>
        )}

        {step === 'mfa_verify' && (
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('login_form.label_mfa_code')}</label>
              <input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus disabled={isLoading} placeholder="000000" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={isLoading || mfaCode.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg text-sm transition-colors">
              {isLoading ? t('login_form.button_busy') : t('login_form.button_confirm')}
            </button>
            <button type="button" onClick={resetToCredentials} className="w-full text-xs text-gray-400 hover:text-gray-600">
              {t('login_form.button_back_to_login')}
            </button>
          </form>
        )}

        {step === 'mfa_setup_scan' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              {t('login_form.mfa_setup_required_notice')}
            </div>
            <p className="text-sm text-gray-600">{t('login_form.mfa_scan_instruction')}</p>
            {qrCode && (
              <img src={`data:image/png;base64,${qrCode}`} alt={t('login_form.mfa_qr_alt')}
                className="w-48 h-48 border border-gray-200 rounded-lg mx-auto" />
            )}
            <details className="text-xs text-gray-400">
              <summary className="cursor-pointer hover:text-gray-600">{t('login_form.mfa_qr_not_working')}</summary>
              <code className="block mt-1 bg-gray-100 rounded px-2 py-1 font-mono break-all select-all">{secret}</code>
            </details>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button onClick={() => { setStep('mfa_setup_confirm'); setError(null); setMfaCode('') }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg text-sm transition-colors">
              {t('login_form.button_next')}
            </button>
          </div>
        )}

        {step === 'change_password' && (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              {t('login_form.first_login_notice')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('login_form.label_new_password')}</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus minLength={8} disabled={isLoading} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('login_form.label_repeat_password')}</label>
              <input type="password" value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                minLength={8} disabled={isLoading} />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={isLoading || newPassword.length < 8}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg text-sm transition-colors">
              {isLoading ? t('login_form.button_saving') : t('login_form.button_set_password')}
            </button>
          </form>
        )}

        {step === 'mfa_setup_confirm' && (
          <form onSubmit={handleMfaSetupConfirm} className="space-y-4">
            <p className="text-sm text-gray-600">{t('login_form.mfa_confirm_instruction')}</p>
            <input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={isLoading || mfaCode.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg text-sm transition-colors">
              {isLoading ? t('login_form.button_busy') : t('login_form.button_activate_mfa')}
            </button>
            <button type="button" onClick={() => { setStep('mfa_setup_scan'); setError(null) }}
              className="w-full text-xs text-gray-400 hover:text-gray-600">
              {t('login_form.button_back')}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
